import { WebglHelper } from './webglHelper';
import { Webgl2Pipeline } from './webgl2Pipeline';
export class ImageStage {

    private vbWebgl2: Webgl2Pipeline;
    private vbHelper: WebglHelper;
    private vertexShaderImage: WebGLShader | null = null;
    private fragmentShaderImage: WebGLShader | null = null;
    private program: WebGLProgram | null = null;
    private backgroundTexture: WebGLTexture | null = null;
    private outputRatio: number;
    private backgroundLocation: WebGLUniformLocation;
    private backgroundScaleLocation: WebGLUniformLocation;
    private backgroundOffsetLocation: WebGLUniformLocation;
    private coverageLocation: WebGLUniformLocation;
    private lightWrappingLocation: WebGLUniformLocation;
    private blendModeLocation: WebGLUniformLocation;
    private gl: WebGL2RenderingContext;
    private video: HTMLVideoElement;
    private options: any;

    /**
     * @function create
     * @static
     * @public
     * @param {Options} options The options (type and url) of the image or the video
     * @param {Webgl2Pipeline} vbWebgl2 The instance of the webgl2 class
     * @returns {ImageStage | null} Initialization of the class
     */
    public static create(options: any, vbWebgl2: Webgl2Pipeline): Promise<ImageStage | null> {
        return new Promise((resolve: any, reject: any) => {
            if (options.backgroundType !== vbWebgl2.VIRTUAL_BACKGROUND_TYPE.IMAGE && options.backgroundType !== vbWebgl2.VIRTUAL_BACKGROUND_TYPE.VIDEO) resolve(null);
            else {
                if (options.backgroundType === vbWebgl2.VIRTUAL_BACKGROUND_TYPE.IMAGE) {
                    const virtualImage = new Image();
                    virtualImage.crossOrigin = 'anonymous';
                    virtualImage.onload = () => { resolve(new ImageStage(virtualImage, vbWebgl2, options)); };
                    virtualImage.onerror = () => { reject(new Error(`[VirtualBackgroundImageStage] -- create -- failed to load image`)); };
                    virtualImage.src = options.imgBg;
                }
                else if (options.backgroundType === vbWebgl2.VIRTUAL_BACKGROUND_TYPE.VIDEO) {
                    const virtualVideo = document.createElement('video');
                    virtualVideo.crossOrigin = 'anonymous';
                    virtualVideo.onloadeddata = () => { resolve(new ImageStage(virtualVideo, vbWebgl2, options)); };
                    virtualVideo.onerror = () => { reject(new Error(`[VirtualBackgroundImageStage] -- create -- failed to load video`)); };
                    virtualVideo.src = options.imgBg;
                    virtualVideo.play();
                    virtualVideo.autoplay = true;
                    virtualVideo.muted = true;
                    virtualVideo.loop = true;
                }

            }
        });
    }

    private constructor(virtualBg: any, vbWebgl2: Webgl2Pipeline, options: any) {
        this.vbWebgl2 = vbWebgl2;
        this.vbHelper = WebglHelper.create();
        const vertexShaderSource = String.raw`#version 300 es
            uniform vec2 u_backgroundScale;
            uniform vec2 u_backgroundOffset;

            in vec2 a_position;
            in vec2 a_texCoord;

            out vec2 v_texCoord;
            out vec2 v_backgroundCoord;

            void main(){
                gl_Position = vec4(a_position * vec2(1.0, - 1.0), 0.0, 1.0);
                v_texCoord = a_texCoord;
                v_backgroundCoord = a_texCoord * u_backgroundScale + u_backgroundOffset;
            }`;

        const fragmentShaderSource = String.raw`#version 300 es

            precision highp float;

            uniform sampler2D u_inputFrame;
            uniform sampler2D u_personMask;
            uniform sampler2D u_background;
            uniform vec2 u_coverage;
            uniform float u_lightWrapping;
            uniform float u_blendMode;

            in vec2 v_texCoord;
            in vec2 v_backgroundCoord;

            out vec4 outColor;

            vec3 screen(vec3 a, vec3 b){
                return 1.0 - (1.0 - a) * (1.0 - b);
            }

            vec3 linearDodge(vec3 a, vec3 b){
                return a + b;
            }

            void main(){
                vec3 frameColor = texture(u_inputFrame, v_texCoord).rgb;
                vec3 backgroundColor = texture(u_background, v_backgroundCoord).rgb;
                float personMask = texture(u_personMask, v_texCoord).a;
                //outColor = vec4(personMask, personMask ,personMask, 1.0);
                float lightWrapMask = 1.0 - max(0.0, personMask - u_coverage.y)/(1.0 - u_coverage.y);
                vec3 lightWrap = u_lightWrapping * lightWrapMask * backgroundColor;
                //outColor = vec4(lightWrap, 1.0);
                frameColor = u_blendMode * linearDodge(frameColor, lightWrap) +
                    (1.0 - u_blendMode) * screen(frameColor, lightWrap);
                personMask = smoothstep(u_coverage.x, u_coverage.y, personMask);
                outColor = vec4(frameColor * personMask + backgroundColor * (1.0 - personMask), 1.0);
            }`;

        const width: number = this.vbWebgl2.outputCanvasElement.width;
        const height: number = this.vbWebgl2.outputCanvasElement.height;

        this.gl = this.vbWebgl2.gl;
        this.vertexShaderImage = this.vbHelper.initShader(this.gl, this.gl.VERTEX_SHADER, vertexShaderSource);
        this.fragmentShaderImage = this.vbHelper.initShader(this.gl, this.gl.FRAGMENT_SHADER, fragmentShaderSource);
        this.program = this.vbHelper.initProgram(this.gl, this.vertexShaderImage, this.fragmentShaderImage, this.vbWebgl2.positionBuffer, this.vbWebgl2.texCoordBuffer);

        this.backgroundScaleLocation = this.gl.getUniformLocation(this.program, 'u_backgroundScale');
        this.backgroundOffsetLocation = this.gl.getUniformLocation(this.program, 'u_backgroundOffset');
        const inputFrameLocation = this.gl.getUniformLocation(this.program, 'u_inputFrame');
        const personMaskLocation = this.gl.getUniformLocation(this.program, 'u_personMask');
        this.backgroundLocation = this.gl.getUniformLocation(this.program, 'u_background');
        this.coverageLocation = this.gl.getUniformLocation(this.program, 'u_coverage');
        this.lightWrappingLocation = this.gl.getUniformLocation(this.program, 'u_lightWrapping');
        this.blendModeLocation = this.gl.getUniformLocation(this.program, 'u_blendMode');
        this.outputRatio = width / height;

        this.gl.useProgram(this.program);
        this.gl.uniform2f(this.backgroundScaleLocation, 1, 1);
        this.gl.uniform2f(this.backgroundOffsetLocation, 0, 0);
        this.gl.uniform1i(inputFrameLocation, 0);
        this.gl.uniform1i(personMaskLocation, 1);
        this.gl.uniform2f(this.coverageLocation, 0, 1);
        this.gl.uniform1f(this.lightWrappingLocation, 0);
        this.gl.uniform1f(this.blendModeLocation, 0);
        let virtualImage: HTMLImageElement = null;
        let virtualVideo: HTMLVideoElement = null;
        this.options = options;

        if (this.options.backgroundType === this.vbWebgl2.VIRTUAL_BACKGROUND_TYPE.IMAGE) {
            virtualImage = virtualBg;

            if (virtualBg) { this.updateBackgroundImage(virtualImage); }
        }
        else if (this.options.backgroundType === this.vbWebgl2.VIRTUAL_BACKGROUND_TYPE.VIDEO) {
            virtualVideo = virtualBg;
            if (virtualBg) { this.updateBackgroundImage(virtualVideo); }
        }
    }

    /**
     * @function render
     * @public
     * @description Assembles the mask with the person's video stream and the virtual background image
     */
    public render(): void {

        this.gl.viewport(0, 0, this.vbWebgl2.outputCanvasElement.width, this.vbWebgl2.outputCanvasElement.height);
        this.gl.useProgram(this.program);
        this.gl.activeTexture(this.gl.TEXTURE1);
        this.gl.bindTexture(this.gl.TEXTURE_2D, this.vbWebgl2.personMaskTexture);

        if (this.backgroundTexture !== null) {
            this.gl.activeTexture(this.gl.TEXTURE2);
            this.gl.bindTexture(this.gl.TEXTURE_2D, this.backgroundTexture);
            this.gl.uniform1i(this.backgroundLocation, 2);

            //Update of the background video
            if (this.options.backgroundType === this.vbWebgl2.VIRTUAL_BACKGROUND_TYPE.VIDEO) {
                this.gl.texSubImage2D(this.gl.TEXTURE_2D, 0, 0, 0, this.video.videoWidth, this.video.videoHeight, this.gl.RGBA, this.gl.UNSIGNED_BYTE, this.video);
            }
        }

        this.gl.bindFramebuffer(this.gl.FRAMEBUFFER, null);
        this.gl.drawArrays(this.gl.TRIANGLE_STRIP, 0, 4);
    }

    /**
     * @function updateCoverage
     * @public
     * @param {[number, number]} coverage
     */
    public updateCoverage(coverage: [number, number]): void {
        this.gl.useProgram(this.program);
        this.gl.uniform2f(this.coverageLocation, coverage[0], coverage[1]);
    }

    /**
     * @function updateLightWrapping
     * @public
     * @param {number} lightWrapping The lightWrapping parameter
     * @description Light wrapping is a compositing technique designed to blend keyed out green screen footage with a background plate
     */
    public updateLightWrapping(lightWrapping: number): void {
        this.gl.useProgram(this.program);
        this.gl.uniform1f(this.lightWrappingLocation, lightWrapping);
    }

    /**
     * @function updateBlendMode
     * @public
     * @param {BlendMode} blendMode The mode that will apply
     * @description determines how the two layers will be blended together.
     */
    public updateBlendMode(blendMode: number): void {
        this.gl.useProgram(this.program);
        this.gl.uniform1f(this.blendModeLocation, blendMode);
    }

    /**
     * @function cleanUp
     * @public
     * @description removes the different components used to make the assembly with the image
     */
    public cleanUp() {
        if (this.backgroundTexture) this.gl.deleteTexture(this.backgroundTexture);
        if (this.program) this.gl.deleteProgram(this.program);
        if (this.fragmentShaderImage) this.gl.deleteShader(this.fragmentShaderImage);
        if (this.vertexShaderImage) this.gl.deleteShader(this.vertexShaderImage);

    }

    /**
     * @function updateBackgroundImage
     * @public
     * @param {any} background An HTMLLImageElement or HTMLVideoElement that contains the virtual background that will be placed behind the person
     * @description Resize the virtual background to the right size
     */
    public updateBackgroundImage(background: any) {
        let backgroundWidth;
        let backgroundHeight;
        let bgWidth;
        let bgHeight;

        if (this.options.backgroundType === this.vbWebgl2.VIRTUAL_BACKGROUND_TYPE.IMAGE) {
            this.backgroundTexture = this.vbHelper.createTexture(this.gl, this.gl.RGBA8, background.naturalWidth, background.naturalHeight, this.gl.LINEAR, this.gl.LINEAR);
            this.gl.texSubImage2D(this.gl.TEXTURE_2D, 0, 0, 0, background.naturalWidth, background.naturalHeight, this.gl.RGBA, this.gl.UNSIGNED_BYTE, background);
            backgroundWidth = background.naturalWidth;
            backgroundHeight = background.naturalHeight;
            bgWidth = background.naturalWidth;
            bgHeight = background.naturalHeight;
        }


        if (this.options.backgroundType === this.vbWebgl2.VIRTUAL_BACKGROUND_TYPE.VIDEO) {
            this.backgroundTexture = this.vbHelper.createTexture(this.gl, this.gl.RGBA8, background.videoWidth, background.videoHeight, this.gl.LINEAR, this.gl.LINEAR);
            this.gl.bindTexture(this.gl.TEXTURE_2D, this.backgroundTexture);
            this.gl.texImage2D(this.gl.TEXTURE_2D, 0, this.gl.RGBA, 1, 1, 0, this.gl.RGBA, this.gl.UNSIGNED_BYTE, new Uint8Array([0, 0, 255, 255]));
            this.video = background;
            backgroundWidth = this.video.videoWidth;
            backgroundHeight = this.video.videoHeight;
            bgWidth = this.video.videoWidth;
            bgHeight = this.video.videoHeight;
        }

        let xOffset = 0;
        let yOffset = 0;

        //Virtual image cropping
        const backgroundRatio = backgroundWidth / backgroundHeight;
        if (backgroundRatio < this.outputRatio) {
            backgroundHeight = backgroundWidth / this.outputRatio;
            yOffset = (bgHeight - backgroundHeight) / 2;
        }
        else {
            backgroundWidth = backgroundHeight * this.outputRatio;
            xOffset = (bgWidth - backgroundWidth) / 2;
        }

        const xScale = backgroundWidth / bgWidth;
        const yScale = backgroundHeight / bgHeight;
        xOffset /= bgWidth;
        yOffset /= bgHeight;

        this.gl.uniform2f(this.backgroundScaleLocation, xScale, yScale);
        this.gl.uniform2f(this.backgroundOffsetLocation, xOffset, yOffset);
    }

}
